import React, { useRef } from 'react';
import { HiMail, HiOutlineExclamationCircle, HiOutlineUserGroup, HiCheck, HiDownload, HiChevronDown, HiUpload } from 'react-icons/hi';
import { Dropdown } from 'flowbite-react';
import { BreadcrumbItem } from '../../DashboardNew/BreadcrumbHeader';
import CustomButton from '../../../components/common/CustomButton';
import { SearchMode, TabType, ViewType } from '../types';
import { useDispatch, useSelector } from 'react-redux';
import { useOrganizationService, useResourcesService } from '../../../services';
import { BreadcrumbsHeader } from "./index";
import { useJwt } from "../../../helpers/common";
import { setOrganizationOnboardSettings } from '../../../redux/slices/organization';
import useIntegrationStatus from "../../../hooks/useIntegrationStatus";
import { MdManageAccounts } from "react-icons/md";
import { setResourceActiveTab, setResourceSearchMode, setResourceSearchTerm } from "../../../redux/slices/resources";
import {
  OnboardingSpotlight,
  OnboardingStepKeyEnum,
  useOnboardingFlattenedTour,
  useOnboardingNavigator,
  useOnboardingRestart,
  useOnboardingRoadmap,
  useOnboardingStep
} from '../../../components/Onboarding';

interface ResourceHeaderProps {
  breadcrumbs: BreadcrumbItem[];
  onBreadcrumbClick?: (index: number, item: BreadcrumbItem) => void;
  onManageInvitesClick?: () => void;
  onCreateTeamClick?: () => void;
  onImportTeamsClick?: () => void;
  onImportUsersClick?: () => void;
  currentView?: ViewType;
  onTeamsWithChangesClick?: () => void;
}

const ResourceHeader: React.FC<ResourceHeaderProps> = ({ breadcrumbs, onBreadcrumbClick, onManageInvitesClick, onCreateTeamClick, onImportTeamsClick, onImportUsersClick, currentView, onTeamsWithChangesClick }) => {
  const jwtData: any = useJwt()
  const dispatch = useDispatch()
  const { isIntegrationActive } = useIntegrationStatus()
  const { updateOrganization } = useOrganizationService();
  const { fetchSuggestedTaskOnOpenSlot, clearSuggestedTaskOnOpenSlot } = useResourcesService();
  const { isCurrent } = useOnboardingRoadmap();
  const [pausedUsers, setPausedUsers] = React.useState(false);
  const [pausedTeams, setPausedTeams] = React.useState(false);
  const { totalSteps: globalTotal, getIndexForStepKey } = useOnboardingFlattenedTour();
  const { goToNextFrom } = useOnboardingNavigator();
  const restartUsersToken = useOnboardingRestart(OnboardingStepKeyEnum.ImportUsers);
  const restartTeamsToken = useOnboardingRestart(OnboardingStepKeyEnum.ImportTeams);

  const importUsersWrapperRef = useRef<HTMLDivElement>(null);
  const importTeamsWrapperRef = useRef<HTMLDivElement>(null);

  const { suggestedTasks, suggestedTasksLoading, resourcePageState, capacityPlanningFilters } = useSelector((state: any) => state.resources);
  const { onboardSettings = {} } = useSelector((state: any) => state.organization);
  const { syncDifferences, syncDifferencesLoading } = useSelector((state: any) => state.azureTeams);
  const { activeTab } = resourcePageState || {};
  const { searchType } = capacityPlanningFilters || {};

  const {
    show: showImportUsersSpotlight,
    complete: completeImportUsers
  } = useOnboardingStep(OnboardingStepKeyEnum.ImportUsers, {
    shouldShow: (isIntegrationActive('azure') || isIntegrationActive('jira')) && isCurrent(OnboardingStepKeyEnum.ImportUsers)
  });
  const {
    show: showImportTeamsSpotlight,
    complete: completeImportTeams
  } = useOnboardingStep(OnboardingStepKeyEnum.ImportTeams, {
    shouldShow: isIntegrationActive('azure') && isCurrent(OnboardingStepKeyEnum.ImportTeams)
  });

  const handleSlotClick = () => {
    fetchSuggestedTaskOnOpenSlot();
  };

  const handleClearSuggestedClick = () => {
    clearSuggestedTaskOnOpenSlot();
  };

  const handleTeamsWithChangesClick = () => {
    dispatch(setResourceActiveTab(TabType.TEAMS));
    dispatch(setResourceSearchTerm(''));
    dispatch(setResourceSearchMode(SearchMode.NAME));
    onTeamsWithChangesClick?.();
  };

  return (
    <div className="h-[65px] flex items-center justify-between gap-2 w-full py-2 px-2 sm:px-4 bg-light-surfacePrimary dark:bg-secondary-dark border-b border-light-border-default dark:border-gray-700">
      <BreadcrumbsHeader
        breadcrumbs={breadcrumbs}
        onBreadcrumbClick={onBreadcrumbClick}
      />
      <div className="flex items-center gap-3">
        {suggestedTasks?.resources &&
          Object.values(suggestedTasks?.resources).every(
            (resource: any) => Object.keys(resource).length === 0,
          ) && (
            <div className="flex items-center justify-center mr-4">
              <div
                className="bg-blue-100/80 dark:bg-blue-900/40 border border-blue-300/50 dark:border-blue-700/50 
                            rounded-lg px-3 py-1.5 backdrop-blur-sm"
              >
                <div className="flex items-center gap-2">
                  <HiOutlineExclamationCircle className="w-4 h-4 text-blue-700 dark:text-blue-400 flex-shrink-0" />
                  <span className="text-xs font-medium text-blue-800 dark:text-blue-200 whitespace-nowrap">
                    No suggestions available
                  </span>
                </div>
              </div>
            </div>
          )}

        {currentView === ViewType.PEOPLE_TEAMS && (
          <>
            {/* Show loading only when API is actually loading AND data exists (not first load) */}
            {syncDifferencesLoading && (
              <div className="flex items-center px-3 py-1.5 rounded-full bg-blue-100 dark:bg-blue-900/30">
                <svg
                  className="animate-spin h-4 w-4 text-blue-600 dark:text-blue-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span className="ml-1.5 text-xs font-semibold text-blue-800 dark:text-blue-200 whitespace-nowrap">
                  Checking teams with changes...
                </span>
              </div>
            )}

            {/* Show teams with changes when loaded and has data */}
            {!syncDifferencesLoading &&
              syncDifferences &&
              syncDifferences.length > 0 && (
                <div
                  className="flex items-center px-3 py-1.5 rounded-full bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-900/30 hover:dark:bg-yellow-900/40 cursor-pointer"
                  onClick={handleTeamsWithChangesClick}
                >
                  <HiOutlineExclamationCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                  <span className="ml-1.5 text-xs font-semibold text-yellow-800 dark:text-yellow-200 whitespace-nowrap">
                    {syncDifferences.length} Team
                    {syncDifferences.length > 1 ? "s" : ""} with Changes
                  </span>
                </div>
              )}

            {/* Show no changes only when loaded and array is empty (not null) */}
            {!syncDifferencesLoading &&
              Array.isArray(syncDifferences) &&
              syncDifferences.length === 0 && (
                <div className="flex items-center px-3 py-1.5 rounded-full bg-green-100 dark:bg-green-900/30">
                  <HiCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
                  <span className="ml-1.5 text-xs font-semibold text-green-800 dark:text-green-200 whitespace-nowrap">
                    No teams have changes
                  </span>
                </div>
              )}

            {activeTab === TabType.TEAMS && (
              <>
                <CustomButton
                  onClick={onCreateTeamClick}
                  leftIcon={<HiOutlineUserGroup className="w-4 h-4" />}
                  className={`
                    ${
                      isIntegrationActive("azure")
                        ? "whitespace-nowrap bg-light-surfacePrimary hover:bg-light-surfaceHover dark:bg-secondary-dark dark:hover:bg-tertiary-dark border dark:border-slate-700  border-light-border-default hover:border-light-border-interactive px-4 py-2 shadow-sm hover:shadow-md transition-all duration-200 !text-black dark:!text-white"
                        : "whitespace-nowrap bg-primary-light hover:bg-primary-hover  px-4 py-2 shadow-sm hover:shadow-md transition-all duration-200 text-white"
                    }
                  `}
                  isLoading={false}
                  disabled={false}
                >
                  Create Team
                </CustomButton>
              </>
            )}

            {activeTab === TabType.PEOPLE &&
              jwtData &&
              jwtData.org_role === "admin" && (
                <>
                  <CustomButton
                    data-onboarding-id="resource-invite-btn"
                    onClick={onManageInvitesClick}
                    className={`
                    ${
                      isIntegrationActive("azure") ||
                      isIntegrationActive("jira")
                        ? "whitespace-nowrap bg-light-surfacePrimary hover:bg-light-surfaceHover dark:bg-secondary-dark dark:hover:bg-tertiary-dark border dark:border-slate-700  border-light-border-default hover:border-light-border-interactive px-4 py-2 shadow-sm hover:shadow-md transition-all duration-200 !text-black dark:!text-white"
                        : "whitespace-nowrap bg-primary-light hover:bg-primary-hover  px-4 py-2 shadow-sm hover:shadow-md transition-all duration-200 text-white"
                    }
                  `}
                  >
                    <HiMail className="w-4 h-4 mr-2" />
                    <span className="hidden lg:inline">Manage Invites</span>
                  </CustomButton>
                </>
              )}

            {/* Combined Import Dropdown for both Teams and People tabs */}
            {((activeTab === TabType.TEAMS && isIntegrationActive("azure")) ||
              (activeTab === TabType.PEOPLE &&
                (isIntegrationActive("azure") ||
                  isIntegrationActive("jira")))) && (
              <div
                ref={
                  activeTab === TabType.TEAMS
                    ? importTeamsWrapperRef
                    : importUsersWrapperRef
                }
              >
                <Dropdown
                  label={
                    <div className="flex items-center gap-2">
                      <HiUpload className="w-4 h-4" />
                      <span>Import</span>
                    </div>
                  }
                  size="sm"
                  data-onboarding-id="resource-team-people-btn"
                  className="whitespace-nowrap  shadow-sm hover:shadow-md transition-all duration-200 text-white"
                  renderTrigger={() => (
                    <CustomButton
                      className="whitespace-nowrap bg-primary-light hover:bg-primary-hover px-4 py-2 shadow-sm hover:shadow-md transition-all duration-200 text-white"
                      disabled={false}
                      isLoading={false}
                    >
                      <div className="flex items-center gap-2">
                        <HiDownload className="w-4 h-4" />
                        <span>Import</span>
                        <HiChevronDown className="w-4 h-4" />
                      </div>
                    </CustomButton>
                  )}
                >
                  {isIntegrationActive("azure") && (
                    <Dropdown.Item onClick={onImportTeamsClick}>
                      Import Teams
                    </Dropdown.Item>
                  )}
                  {(isIntegrationActive("azure") ||
                    isIntegrationActive("jira")) && (
                    <Dropdown.Item onClick={onImportUsersClick}>
                      Import People
                    </Dropdown.Item>
                  )}
                </Dropdown>
              </div>
            )}
          </>
        )}

        {/* Suggest Work Items in Open Slots button - HIDDEN
        {currentView === ViewType.CAPACITY && searchType !== 'sprint' && (
          <CustomButton
            onClick={suggestedTasks?.resources ? handleClearSuggestedClick : handleSlotClick}
            className={`
              ${jwtData && jwtData.org_role === 'admin'
                ? "whitespace-nowrap bg-light-surfacePrimary hover:bg-light-surfaceHover dark:bg-secondary-dark dark:hover:bg-tertiary-dark border dark:border-slate-700  border-light-border-default hover:border-light-border-interactive px-4 py-2 shadow-sm hover:shadow-md transition-all duration-200 !text-black dark:!text-white"
                : "whitespace-nowrap bg-primary-light hover:bg-primary-hover  px-4 py-2 shadow-sm hover:shadow-md transition-all duration-200 text-white"}
            `}
          >
            {suggestedTasks?.resources ? (
              <>
                <span className="hidden lg:inline">
                  Clear Suggested Work Items
                </span>
                <span className="lg:hidden">
                  Clear Work Items
                </span>
              </>
            ) : (
              <div className="flex flex-row gap-2 items-center justify-center">
                {suggestedTasksLoading && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}

                <span className="hidden lg:inline">
                  Suggest Work Items in Open Slots
                </span>

                <span className="lg:hidden">
                  Suggest Work Items
                </span>
              </div>
            )}
          </CustomButton>
        )}
        */}

        {/* Manage button - always visible in capacity view for admins */}
        {currentView === ViewType.CAPACITY &&
          jwtData &&
          jwtData.org_role === "admin" && (
            <CustomButton
              onClick={onManageInvitesClick}
              data-onboarding-id="resource-manage-btn"
              variant={"primary"}
              leftIcon={<MdManageAccounts className="w-5 h-5" />}
              className={
                "bg-primary-light dark:bg-primary-dark hover:bg-primary-hover"
              }
              isLoading={false}
              disabled={false}
            >
              Manage
            </CustomButton>
          )}
      </div>

      {activeTab === TabType.PEOPLE &&
        showImportUsersSpotlight &&
        !pausedUsers && (
          <OnboardingSpotlight
            key={`users-${restartUsersToken}`}
            targetRef={importUsersWrapperRef}
            message={
              isIntegrationActive("jira")
                ? "Import people from Jira to track assigned work items"
                : "Import people from Azure DevOps to track assigned work items"
            }
            onDismiss={() => {
              goToNextFrom(OnboardingStepKeyEnum.ImportUsers);
              setPausedUsers(false);
              completeImportUsers();
            }}
            onCompleteOnly={() => {
              setPausedUsers(true);
              completeImportUsers();
            }}
            onClose={async () => {
              const next = { ...(onboardSettings || {}), dismissed: true };
              dispatch(setOrganizationOnboardSettings(next));
              await updateOrganization(
                jwtData?.org_id,
                { onboard_settings: next },
                true,
              );
            }}
            variant="spotlight"
            stepIndex={Math.max(
              0,
              getIndexForStepKey(OnboardingStepKeyEnum.ImportUsers),
            )}
            totalSteps={globalTotal}
            onBack={() => {
              /* dismiss-only */
            }}
            onSkip={() => {
              completeImportUsers();
            }}
          />
        )}

      {false &&
        showImportTeamsSpotlight &&
        !pausedTeams && (
          <OnboardingSpotlight
            key={`teams-${restartTeamsToken}`}
            targetRef={importTeamsWrapperRef}
            message="Import teams to plan capacity and allocations with confidence."
            onDismiss={() => {
              goToNextFrom(OnboardingStepKeyEnum.ImportTeams);
              setPausedTeams(false);
              completeImportTeams();
            }}
            onCompleteOnly={() => {
              setPausedTeams(true);
              completeImportTeams();
            }}
            onClose={async () => {
              const next = { ...(onboardSettings || {}), dismissed: true };
              dispatch(setOrganizationOnboardSettings(next));
              await updateOrganization(
                jwtData?.org_id,
                { onboard_settings: next },
                true,
              );
            }}
            variant="spotlight"
            stepIndex={Math.max(
              0,
              getIndexForStepKey(OnboardingStepKeyEnum.ImportTeams),
            )}
            totalSteps={globalTotal}
            onBack={() => {
              /* dismiss-only */
            }}
            onSkip={() => {
              completeImportTeams();
            }}
          />
        )}
    </div>
  );
};

export default ResourceHeader; 